import { Trans } from '@mbarzda/solid-i18next'
import type { Component } from 'solid-js'
import { createSignal, For, Index, Show } from 'solid-js'
import type { SimplePeriodType, CustomDateRange, CompareMode } from '../../../shared/helpers/date.functions'
import CustomRangeDialog from './CustomRangeDialog'
import { useUser } from '../../../shared/providers/user-provider'

interface PeriodSelectorProps {
  selectedPeriod: SimplePeriodType
  onPeriodChange: (period: SimplePeriodType) => void
  customDateRange?: CustomDateRange
  onCustomRangeChange?: (from: Date, to: Date) => void
  showCompareSelector?: boolean
  compareMode?: CompareMode
  onCompareModeChange?: (mode: CompareMode) => void
  compareCustomRange?: CustomDateRange
  onCompareCustomRangeChange?: (from: Date, to: Date) => void
}

type PeriodOption = { value: SimplePeriodType; label: string }
type CompareOption = { value: CompareMode; label: string }

const PERIOD_GROUPS: PeriodOption[][] = [
  [
    { value: 'last_7_days', label: 'ls_period_last7Days' },
    { value: 'last_30_days', label: 'ls_period_last30Days' },
    { value: 'last_90_days', label: 'ls_period_last90Days' }
  ],
  [
    { value: 'last_month', label: 'ls_period_lastMonth' },
    { value: 'last_year', label: 'ls_period_lastYear' }
  ],
  [{ value: 'all_time', label: 'ls_period_allTime' }]
]

const COMPARE_OPTIONS: CompareOption[] = [
  { value: 'previous_period', label: 'ls_period_previousPeriod' },
  { value: 'custom_range', label: 'ls_period_customRange' }
]

const ALL_PERIOD_OPTIONS = PERIOD_GROUPS.flat()

const PeriodSelector: Component<PeriodSelectorProps> = (props) => {
  const { currentOrganization } = useUser()
  const [isOpen, setIsOpen] = createSignal(false)
  const [isCompareOpen, setIsCompareOpen] = createSignal(false)
  const [isCustomRangeDialogOpen, setIsCustomRangeDialogOpen] = createSignal(false)
  const [isCompareCustomRangeDialogOpen, setIsCompareCustomRangeDialogOpen] = createSignal(false)

  const firstAllowedDate = () => {
    const dateStr = currentOrganization()?.first_data_date
    return dateStr ? new Date(dateStr) : undefined
  }

  const formatDateRange = (range: CustomDateRange): string => {
    const formatDate = (date: Date): string => {
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = String(date.getFullYear()).slice(-2)
      return `${month}/${day}/${year}`
    }
    return `${formatDate(range.from)} - ${formatDate(range.to)}`
  }

  const selectedLabel = () => {
    if (props.selectedPeriod === 'custom' && props.customDateRange) {
      return formatDateRange(props.customDateRange)
    }
    const option = ALL_PERIOD_OPTIONS.find((opt) => opt.value === props.selectedPeriod)
    return option ? option.label : 'ls_period_selectPeriod'
  }

  const isCustomRange = () => props.selectedPeriod === 'custom' && props.customDateRange

  const compareLabel = () => {
    const mode = props.compareMode || 'previous_period'
    if (mode === 'custom_range' && props.compareCustomRange) {
      return formatDateRange(props.compareCustomRange)
    }
    const option = COMPARE_OPTIONS.find((opt) => opt.value === mode)
    return option ? option.label : 'ls_period_previousPeriod'
  }

  const isCompareCustomRange = () =>
    (props.compareMode || 'previous_period') === 'custom_range' && props.compareCustomRange

  const handleSelect = (period: SimplePeriodType) => {
    props.onPeriodChange(period)
    setIsOpen(false)
  }

  const handleCustomRangeClick = () => {
    setIsOpen(false)
    setIsCustomRangeDialogOpen(true)
  }

  const handleCustomRangeApply = (from: Date, to: Date) => {
    if (props.onCustomRangeChange) {
      props.onCustomRangeChange(from, to)
    }
    props.onPeriodChange('custom')
    setIsCustomRangeDialogOpen(false)
  }

  const handleCompareSelect = (mode: CompareMode) => {
    if (mode === 'custom_range') {
      setIsCompareOpen(false)
      setIsCompareCustomRangeDialogOpen(true)
    } else {
      if (props.onCompareModeChange) {
        props.onCompareModeChange(mode)
      }
      setIsCompareOpen(false)
    }
  }

  const handleCompareCustomRangeApply = (from: Date, to: Date) => {
    if (props.onCompareCustomRangeChange) {
      props.onCompareCustomRangeChange(from, to)
    }
    if (props.onCompareModeChange) {
      props.onCompareModeChange('custom_range')
    }
    setIsCompareCustomRangeDialogOpen(false)
  }

  const closeDropdowns = () => {
    setIsOpen(false)
    setIsCompareOpen(false)
  }

  return (
    <>
      <div class="flex items-center gap-3">
        {/* Primary Period Selector */}
        <div class="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen())}
            class="inline-flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-w-[204px]"
          >
            <Show when={isCustomRange()} fallback={<Trans key={selectedLabel()} />}>
              {formatDateRange(props.customDateRange!)}
            </Show>
            <svg
              class="h-4 w-4 text-gray-400 transition-transform"
              classList={{ 'rotate-180': isOpen() }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <Show when={isOpen()}>
            <div class="absolute left-0 z-20 mt-2 w-48 origin-top-left rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div class="py-1">
                <Index each={PERIOD_GROUPS}>
                  {(group, groupIndex) => (
                    <>
                      <For each={group()}>
                        {(option) => (
                          <button
                            type="button"
                            onClick={() => handleSelect(option.value)}
                            class="block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100"
                            classList={{
                              'bg-gray-100 text-gray-900 font-medium': option.value === props.selectedPeriod,
                              'text-gray-700': option.value !== props.selectedPeriod
                            }}
                          >
                            <Trans key={option.label} />
                          </button>
                        )}
                      </For>
                      <Show when={groupIndex < PERIOD_GROUPS.length - 1}>
                        <div class="my-1 border-t border-gray-200" />
                      </Show>
                    </>
                  )}
                </Index>

                {/* Custom Range option */}
                <div class="my-1 border-t border-gray-200" />
                <button
                  type="button"
                  onClick={handleCustomRangeClick}
                  class="block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100"
                  classList={{
                    'bg-gray-100 text-gray-900 font-medium': props.selectedPeriod === 'custom',
                    'text-gray-700': props.selectedPeriod !== 'custom'
                  }}
                >
                  <Trans key="ls_period_customRange" />
                </button>
              </div>
            </div>
          </Show>
        </div>

        <Show when={props.showCompareSelector !== false}>
          {/* VS Label */}
          <span class="text-sm font-medium text-gray-500">
            <Trans key="ls_period_vs" />
          </span>

          {/* Compare Period Selector */}
          <div class="relative">
            <button
              type="button"
              onClick={() => setIsCompareOpen(!isCompareOpen())}
              class="inline-flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 min-w-[204px]"
            >
              <Show when={isCompareCustomRange()} fallback={<Trans key={compareLabel()} />}>
                {formatDateRange(props.compareCustomRange!)}
              </Show>
              <svg
                class="h-4 w-4 text-gray-400 transition-transform"
                classList={{ 'rotate-180': isCompareOpen() }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <Show when={isCompareOpen()}>
              <div class="absolute left-0 z-20 mt-2 w-48 origin-top-left rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div class="py-1">
                  <For each={COMPARE_OPTIONS}>
                    {(option) => (
                      <button
                        type="button"
                        onClick={() => handleCompareSelect(option.value)}
                        class="block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100"
                        classList={{
                          'bg-gray-100 text-gray-900 font-medium':
                            option.value === (props.compareMode || 'previous_period'),
                          'text-gray-700': option.value !== (props.compareMode || 'previous_period')
                        }}
                      >
                        <Trans key={option.label} />
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>
          </div>
        </Show>

        {/* Click outside handler */}
        <Show when={isOpen() || isCompareOpen()}>
          <div class="fixed inset-0 z-10" onClick={closeDropdowns} />
        </Show>
      </div>

      {/* Custom Range Dialog */}
      <CustomRangeDialog
        isOpen={isCustomRangeDialogOpen()}
        onClose={() => setIsCustomRangeDialogOpen(false)}
        onApply={handleCustomRangeApply}
        initialFrom={props.customDateRange?.from}
        initialTo={props.customDateRange?.to}
        firstAllowedDate={firstAllowedDate()}
      />

      {/* Compare Custom Range Dialog */}
      <CustomRangeDialog
        isOpen={isCompareCustomRangeDialogOpen()}
        onClose={() => setIsCompareCustomRangeDialogOpen(false)}
        onApply={handleCompareCustomRangeApply}
        initialFrom={props.compareCustomRange?.from}
        initialTo={props.compareCustomRange?.to}
        firstAllowedDate={firstAllowedDate()}
      />
    </>
  )
}

export default PeriodSelector
